import { useEffect, useRef, useState } from 'react';
import { useStylesheets } from 'hooks/useStylesheets';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { usePopupKeys } from 'hooks/usePopupKeys';
import { getBootUserId } from 'hooks/useDashboardData';
import { useGetDashboardUserQuery } from 'app/services/dashboardApi';
import GlobalHeader from 'pages/dashboard/components/GlobalHeader';
import SettingsMenu from './components/SettingsMenu';
import SubmittalTab from './components/submittal/SubmittalTab';
import ProposalTab from './components/proposal/ProposalTab';
import ColorTab from './components/ColorTab';
import UserSettingsFooter from './components/UserSettingsFooter';
import { useFilterNavigation } from './useFilterNavigation';
import { useUserSettingsTab, type UserSettingsTab } from './useUserSettingsTab';

/**
 * User-settings CSS — the exact legacy cascade, in order, one `useStylesheets`
 * call:
 *   header.css → user_settings/skeleton.css → common.css → user_settings/styles.css
 *
 * The first two come from `templates/user_settings/styles.html:2-3`; the last
 * two are `require.css!` calls inside `mvc/app.js:361-362`, which run after the
 * `<head>` links. `react.html` already ships common.css + header.css globally;
 * re-injecting all four here (appended after the globals) reproduces the
 * last-occurrence order.
 *
 * ★ BOOTSTRAP ARRIVES ON ITS OWN — do not add a fifth entry.
 * Unlike the sizer and partners shells (whose `styles.html` links bootstrap
 * explicitly), `user_settings/styles.html` has only the two links above.
 * Bootstrap-3 reaches the page through `styles.css:2`
 * `@import "../../../../../static/vendor/bootstrap-3.3.4-dist/css/bootstrap.min.css"`,
 * which resolves to `/static/vendor/…` and lands BEFORE styles.css's own rules.
 * The page needs it (`col-xs-*`, `nav-tabs`, `tab-content`, `.hidden`), and it
 * comes for free with the fourth sheet.
 *
 * ★ NOT LOADED: `css/user_settings/proposal.css` — 31 KB referenced by no
 * template and no `require.css!` call (discovery §10 dead files).
 */
const USER_SETTINGS_CSS = [
  '/static/css/common/header.css',
  '/static/css/user_settings/skeleton.css',
  '/static/css/common/common.css',
  '/static/css/user_settings/styles.css',
];

/** `.user_settings-heading__title` text — `api/api.js:73,91,110`. */
const HEADING_TITLE: Record<UserSettingsTab, string> = {
  submittal: 'Submittal Format',
  proposal: 'Proposal Format',
  color: 'Color',
};

/** `document.title` — `api/api.js:118-125` (`view_title[view] + ": User Settings"`). */
const DOCUMENT_TITLE: Record<UserSettingsTab, string> = {
  submittal: 'Submittal: User Settings',
  proposal: 'Proposal: User Settings',
  color: 'Color: User Settings',
};

/** `<body class>` — `api/api.js:131-132`, the pane id, not the tab name. */
const BODY_CLASS: Record<UserSettingsTab, string> = {
  submittal: 'submittal_format',
  proposal: 'proposal_format',
  color: 'color_format',
};

/**
 * User Settings (`/user_settings/` — Backbone app `user_settings`), wave U-a.
 *
 * U-a is the page shell + the shared header + the hash-tab contract + the whole
 * Colour tab, i.e. the smallest slice that still contains a real server write.
 * `tmpl/main.tmpl` (48 lines) is rendered 1:1: the heading strip, the collapsible
 * three-tab rail, and the three `tab-content` panes.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★ BOOT SHAPE — ALL THREE TABS ARE CONSTRUCTED, ALWAYS. NEVER MAKE THEM LAZY.
 *
 * `api/api.js:57-121`: whichever tab the hash selects, the OTHER TWO are
 * constructed too (`submittal_format(false)` / `proposalFormat(false)` /
 * `color(false)` — the `false` only suppresses the title/body-class side
 * effects, not the construction). So `/user_settings/#color` still builds the
 * 919 KB submittal DOM and still fires all 15 boot requests, and switching tabs
 * afterwards is PURE show/hide with ZERO network (verified in discovery §3).
 *
 * The three panes below are therefore always MOUNTED and only ever toggled with
 * Bootstrap's `.hidden` class. `{activeTab === x && <Tab/>}` would be wrong on
 * two counts: it would move that tab's boot requests to first-open, and it
 * would put network traffic on a tab switch.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * ★ HASH BEHAVIOUR lives in `useUserSettingsTab.ts` — read that file before
 * touching anything hash-shaped. Short version: `controller/router.js` is dead
 * code, so the hash is read ONCE at boot and thereafter only WRITTEN; Back and
 * Forward move the URL without moving the view, and that is required parity.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * SEAMS — what the later waves plug into, and where:
 *   U-b  read-only Submittal          → `#submittal_format` pane below
 *   U-c  Submittal UDF lifecycle      → inside U-b's tab (popups, PDF upload)
 *   U-d  Submittal format drag & drop → inside U-b's format rail
 *   U-e  the whole Proposal feature   → `#proposal_format` pane below  ✓ landed
 *   U-f  keyboard + edge cases                                        ✓ landed
 *        · the ↑/↓ walk (`view/main.js:51-62, 100-174`) → `useFilterNavigation`
 *          below. Extracted to a sibling hook rather than inlined here purely
 *          for size — it is still mounted by the SHELL, which is what the seam
 *          required, because it reaches into whichever tab the live hash names.
 *          ★ Q2 (arrows throw with no hash) is contained inside that hook.
 *        · the Esc/Enter popup keys (`view/main.js:40-50`) → `usePopupKeys`
 *          below, in `blanket` mode. ★ Q17 (three duplicate listeners) is
 *          answered there.
 *        · THE SWITCH — `django/user_settings/urls.py:7` `UserSettingsView` →
 *          `ReactAppAuthView`, plus deleting the DORMANT marker in `App.tsx` —
 *          is the ORCHESTRATOR's, not a builder's. Still open at U-f's end.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const UserSettingsPage = () => {
  const { tab, openTab } = useUserSettingsTab();

  // `api/api.js:125` `$(document).prop('title', …)`. The Django `<title>` block
  // says "User Settings"; the tab side effect overwrites it synchronously on
  // boot, so the tab-qualified string is what a user ever sees.
  useDocumentTitle(DOCUMENT_TITLE[tab]);

  // Gate first paint on the route CSS — these sheets are injected AFTER React
  // mounts, so without the gate the whole page paints as raw unstyled text in
  // the load gap (the FOUC class already fixed on dashboard/project/sizer/
  // partners). Legacy loads all of them render-blocking in <head>.
  const cssLoaded = useStylesheets(USER_SETTINGS_CSS);

  // `self.opened` (`view/main.js:22`) — starts open.
  const [collapsed, setCollapsed] = useState(false);

  /**
   * ★ U-f — the ↑/↓ `keydown.filterNavigation` walk over the format rail and the
   * Defined-Fields library, for BOTH tabs. It is keyed by the LIVE
   * `window.location.hash`, not by `tab`, which is why it is mounted here with
   * no arguments — see `useFilterNavigation.ts` for the whole contract and for
   * ★ Q2.
   */
  useFilterNavigation();

  /**
   * ★ U-f — Esc / Enter, `view/main.js:40-50`:
   *     if (e.which == 27) $('.popup-wrapper.active').remove();
   *     if (e.which == 13) $('.popup-wrapper.active button[autofocus="true"]')
   *                          .trigger('click');
   *
   * ★★ Q17 — LEGACY REGISTERS THIS BLOCK **THREE TIMES**, AND ONE MOUNT IS THE
   * FAITHFUL REPRODUCTION OF IT.
   *
   * `main.js:41`, `submittal.js:152` and `proposal.js:133` each run the same
   * `$('body').keyup(…)` — unnamespaced, never `.off()`ed, and (unlike the
   * arrow listener two lines above them in `main.js`) not de-duplicated. Census
   * taken on the live legacy page: `jQuery._data(document.body, 'events').keyup`
   * has **3** handlers, all with no namespace, while `.keydown` has exactly one
   * `filterNavigation`.
   *
   * What three duplicates OBSERVABLY cause: nothing. Measured with Q4's two
   * stacked delete popups open —
   *   · Esc: the first handler's `.remove()` empties the set, so the 2nd and 3rd
   *     match zero elements. Result 0 active popups, both nodes gone from the
   *     DOM, and NO `DELETE` on the wire. One run and three runs are identical
   *     because `.remove()` is idempotent.
   *   · Enter: `.popup-wrapper.active button[autofocus="true"]` matched **0**
   *     elements (the page's only `autofocus` is the colour picker `<input>`,
   *     `color.tmpl:48`, which is not a button and not inside a popup), so all
   *     three fire and all three do nothing. Both popups stayed open.
   * Mounting the React hook three times would NOT be more faithful — this hook
   * CLICKS each popup's Cancel button rather than ripping the node out, so a
   * triple mount would invoke three React state updates per Esc for an outcome
   * legacy reaches once. The observable outcome is what parity means here.
   *
   * ★ WHY `blanket: true`. The default mode acts on the TOPMOST active popup
   * only and exempts the header's `.component-selector-popup`. Legacy here has
   * neither restriction — `$('.popup-wrapper.active')` is the whole set, with no
   * filter, exactly like the sizer's own blanket handler that this option was
   * built for. It matters on this page: Q4 opens two popups at once, and the
   * header's Ratings popup, if the user has opened it, is a `.popup-wrapper
   * .active` that legacy's Esc removes too. Dismissing it through its own close
   * button (what `blanket` does) also keeps `independent_rating_popup_state`
   * correct — the Phase 4 decision recorded in `usePopupKeys.ts`, taken there
   * for the same reason.
   *
   * KNOWN, INHERITED DEVIATIONS from the shared hook, neither introduced here:
   * it listens on `keydown` where legacy listens on `keyup`, and it returns
   * early when the event target is `contentEditable` (this page has two — the
   * UDF title and content editors — but a popup being open means focus is not
   * in them).
   */
  usePopupKeys({ blanket: true });

  /**
   * ★ Q16 — `api/api.js:131-132` does `$('body').removeClass()` and THEN
   * `addClass(view)`. It does not add a class, it REPLACES the whole class
   * attribute, destroying anything else on `<body>`. Reproduced literally.
   * The original value is captured once and put back on unmount (a React-route
   * courtesy; the Backbone page never unmounts).
   */
  const originalBodyClass = useRef<string | null>(null);
  useEffect(() => {
    if (originalBodyClass.current === null) originalBodyClass.current = document.body.className;
    document.body.className = BODY_CLASS[tab];
  }, [tab]);
  useEffect(
    () => () => {
      if (originalBodyClass.current !== null) document.body.className = originalBodyClass.current;
    },
    []
  );

  // `userSettingsBase.html:15` — `<body data-user_id="{{user.id}}">`.
  // `react.html` has no such attribute; legacy header code reads it.
  useEffect(() => {
    const id = getBootUserId();
    if (id != null) document.body.setAttribute('data-user_id', String(id));
    return () => {
      document.body.removeAttribute('data-user_id');
    };
  }, []);

  /**
   * Height chain. `styles.css:1629` sizes `#ksdm-content` off the
   * `common.css:1620 html{height:100%}` → `styles.css:1456 body{height:100%}`
   * chain, and `.user_settings`, `.user_settings-tab__content` and
   * `.reports-color-list`'s `max-height: calc(100% - 105px)` all hang off it.
   * `#react-root` sits between body and `#ksdm-content` and would break the
   * chain, so it is pinned to 100% — the same repair the reports, AHU and sizer
   * routes use.
   */
  useEffect(() => {
    const root = document.getElementById('react-root');
    const prev = root?.style.height ?? '';
    if (root) root.style.height = '100%';
    return () => {
      if (root) root.style.height = prev;
    };
  }, []);

  // `fn['current.user']` — GET /api/v1.0/users/<id>. `GlobalHeader` subscribes
  // to the same query, so RTK Query dedupes it into the single request legacy
  // makes at t=14ms.
  const bootUserId = getBootUserId();
  const { data: user } = useGetDashboardUserQuery(bootUserId as number, { skip: bootUserId == null });

  return (
    <>
      {/* header.tmpl's ONLY use of `app_name == 'user_settings'` is suppressing
          `#workflow_popup_button` (header.tmpl:33-38). Nothing is marked
          `active`, and `userSettingsBase.html:17` renders
          `<header id="ksdm-header" class="">` — the empty shell-class branch. */}
      <GlobalHeader appName="user_settings" />

      {/* `styles.css:1629 #ksdm-content{height:calc(100% - 63px)}`. The bundled
          dashboard CSS leaks `:global(body #ksdm-content){height:calc(100vh -
          53px)}` (`StatusesRail.module.scss:22`), a higher-specificity rule that
          wins on EVERY React route; carrying the legacy value inline out-ranks
          it without touching the dashboard sheet — the same route-scoped repair
          the AHU board and sizer already use. */}
      <div id="ksdm-content" style={{ height: 'calc(100% - 63px)' }}>
        {cssLoaded && (
          // `main.tmpl:1` + `collapseMenu`'s `.ml-50` (`styles.css:1651`).
          <div className={`user_settings${collapsed ? ' ml-50' : ''}`}>
            {/* main.tmpl:2-8 — three nested col-xs-12 wrappers, as written. */}
            <div className="col-xs-12 user_settings-heading">
              <div className="col-xs-12">
                <div className="col-xs-12">
                  <span className="user_settings-heading__title">{HEADING_TITLE[tab]}</span>
                </div>
              </div>
            </div>

            <SettingsMenu
              activeTab={tab}
              onOpenTab={openTab}
              collapsed={collapsed}
              onToggleCollapse={() => setCollapsed((c) => !c)}
            />

            {/* `submittal/view/submittal.js` (951) + `submittal.tmpl` (320) —
                waves U-b (read-only) and U-c (the UDF lifecycle + PDF upload).
                Mounted UNCONDITIONALLY (see the BOOT SHAPE note above): its 5
                boot fetches must fire whatever the active tab is, and switching
                tabs afterwards must be zero-network.
                ★ Q3: `submittal.tmpl:1`'s root is `<div id="submittal">`, which
                collides with the rail anchor above — three elements end up
                sharing that id on the real page (the third is the proposal
                pane's root, U-e). Reproduced inside `SubmittalTab`.
                ── SEAM U-d ────────────────────────────────────────────────
                the format rail's WRITES — reorder, drop-to-create and row
                delete (§6 W7/W8/W9) — plug into `components/submittal/
                FormatRail.tsx`, which already carries the markup, the `data-*`
                payload and the `.empty-row` sentinel they need. */}
            <div
              id="submittal_format"
              className={`col-xs-12 tab-content user_settings-tab__content${
                tab === 'submittal' ? '' : ' hidden'
              }`}
            >
              <SubmittalTab active={tab === 'submittal'} />
            </div>

            {/* `proposal/view/proposal.js` (729) + `proposal.tmpl` (416) —
                wave U-e, the whole feature. Mounted UNCONDITIONALLY, exactly
                like the submittal pane: its FOUR boot fetches must fire whatever
                the active tab is, and switching tabs afterwards must be
                zero-network.
                ★ Q13: the rail's label class is `prօposal_format_field` with a
                U+0585 ARMENIAN SMALL LETTER OH. ★ Q3 again: the pane's inner
                root is ALSO `<div id="submittal">` — which is what makes every
                `$('#proposal …')` lookup inside the legacy view miss (see
                `ProposalTab`'s banner). ★ Q12 lives on its drag-created rows. */}
            <div
              id="proposal_format"
              className={`col-xs-12 tab-content user_settings-tab__content proposal-tab__content${
                tab === 'proposal' ? '' : ' hidden'
              }`}
            >
              <ProposalTab active={tab === 'proposal'} />
            </div>

            <div
              id="color_format"
              className={`col-xs-12 tab-content user_settings-tab__content${
                tab === 'color' ? '' : ' hidden'
              }`}
            >
              {/* `color/view/color.js` is constructed only after
                  `fn['current.user']` resolves (it is one of its `Promise.all`
                  members), which is why the palette GET lands after /users/<id>
                  in the boot trace. Gating on `user` reproduces that ordering —
                  it is NOT a lazy tab. */}
              {user && (
                <ColorTab userId={user.id} reportColor={String(user.report_color ?? '')} />
              )}
            </div>
          </div>
        )}
      </div>

      <UserSettingsFooter />
    </>
  );
};

export default UserSettingsPage;
